import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, SectionTitle } from "@/components/Kpi";
import { inputCls } from "@/components/Filters";
import { api, apiError } from "@/lib/api";
import { eurShort, num } from "@/lib/format";

const bottone = "px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200";
const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto",
  "settembre", "ottobre", "novembre", "dicembre"];

/** Riga che spiega una proiezione del lungo con i numeri di oggi. */
const Formula = ({ titolo, s, unitaGiorni, testId }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm" data-testid={testId}>
    <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{titolo}</div>
    <div className="font-mono text-slate-800">
      {s.escluso_dealer ? `(${eurShort(s.valore)} − ${eurShort(s.escluso_dealer)} dealer fermi` : `(${eurShort(s.valore)}`}
      {s.giorni_anomali ? ` − ${eurShort(s.anomalo)} gg anomali` : ""}
      {`) ÷ ${s.trascorsi} ${unitaGiorni} trascorsi × ${s.totali} ${unitaGiorni}`}
      {s.giorni_anomali ? ` + ${eurShort(s.anomalo)}` : ""}
      {s.escluso_dealer ? ` + ${eurShort(s.escluso_dealer)}` : ""}
      {" = "}<b>{eurShort(s.proiezione)}</b>
    </div>
  </div>
);

const Scelta = ({ label, value, onChange, opzioni, testId }) => (
  <div>
    <label className="text-xs text-slate-500 block mb-1">{label}</label>
    <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      {Object.entries(opzioni).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
  </div>
);

export default function ProiezioniImpostazioni() {
  const [dati, setDati] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [dealerFermi, setDealerFermi] = useState("");
  const [nuovoGiorno, setNuovoGiorno] = useState("");
  const [salvataggio, setSalvataggio] = useState(false);

  const load = () => api.get("/proiezioni").then((r) => {
    setDati(r.data);
    setCfg(r.data.config);
    setDealerFermi(r.data.lungo.dealer_senza_proiezione.map((d) => d.codice).join(", "));
  }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  if (!dati || !cfg) return <div className="text-sm text-slate-500">Caricamento…</div>;
  const setL = (k, v) => setCfg((c) => ({ ...c, lungo: { ...c.lungo, [k]: v } }));
  const setC = (k, v) => setCfg((c) => ({ ...c, corto: { ...c.corto, [k]: v } }));
  const unita = (m) => (m === "calendario" ? "gg" : "gg lav.");
  const L = dati.lungo; const C = dati.corto; const P = C.previsioni;

  const salva = async () => {
    setSalvataggio(true);
    try {
      await api.put("/proiezioni", { lungo: cfg.lungo, corto: cfg.corto,
        dealer_senza_proiezione: dealerFermi.split(/[,\s;]+/).map((c) => c.trim()).filter(Boolean) });
      toast.success("Proiezioni aggiornate: dashboard e box ricalcolati");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setSalvataggio(false); }
  };

  return (
    <div className="space-y-6" data-testid="proiezioni-page">
      <SectionTitle sub="Formule attive per proiezioni e previsioni, con i numeri di oggi. I parametri si applicano subito a dashboard, box budget, tachimetri e PDF.">
        Proiezioni e previsioni
      </SectionTitle>

      <Card className="p-6 space-y-5" data-testid="proiezioni-lungo">
        <div>
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-[#2E1A8F]">Canale lungo</h3>
          <p className="text-sm text-slate-500 mt-1">
            Proiezione = (progressivo − dealer senza proiezione − giorni anomali) ÷ giorni trascorsi × giorni del periodo,
            poi si risommano a consuntivo i giorni anomali e il liquidato dei dealer fermi (il loro caricato esce del tutto).
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Formula titolo={`Previsione chiusura anno · caricato (${L.anno.caricato.trascorsi}/${L.anno.caricato.totali})`}
            s={L.anno.caricato} unitaGiorni={unita(L.metodo)} testId="formula-lungo-anno-caricato" />
          <Formula titolo="Previsione chiusura anno · liquidato" s={L.anno.liquidato} unitaGiorni={unita(L.metodo)}
            testId="formula-lungo-anno-liquidato" />
          <Formula titolo={`Previsione chiusura ${MESI[L.mese.n - 1]} · caricato`} s={L.mese.caricato}
            unitaGiorni={unita(L.metodo)} testId="formula-lungo-mese-caricato" />
          <Formula titolo={`Previsione chiusura ${MESI[L.mese.n - 1]} · liquidato`} s={L.mese.liquidato}
            unitaGiorni={unita(L.metodo)} testId="formula-lungo-mese-liquidato" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
          <Scelta label="Metodo di proiezione" value={cfg.lungo.metodo} onChange={(v) => setL("metodo", v)}
            opzioni={dati.metodi} testId="lungo-metodo" />
          <div className="md:col-span-2">
            <label className="text-xs text-slate-500 block mb-1">Dealer senza proiezione (codici, separati da virgola)</label>
            <input className={inputCls} value={dealerFermi} onChange={(e) => setDealerFermi(e.target.value)}
              data-testid="lungo-dealer-fermi" />
            <div className="text-xs text-slate-500 mt-1">
              {L.dealer_senza_proiezione.map((d) => `${d.nome} (${d.codice})`).join(" · ") || "nessuno"}
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">
            Giorni anomali (esclusi dalla base di proiezione e sommati a consuntivo)
          </label>
          <div className="flex flex-wrap items-center gap-2">
            {(cfg.lungo.giorni_anomali || []).map((g) => (
              <span key={g} className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-300 px-3 py-1 text-xs font-mono"
                data-testid={`lungo-anomalo-${g}`}>
                {g}
                <button type="button" className="text-amber-700 hover:text-red-600"
                  onClick={() => setL("giorni_anomali", cfg.lungo.giorni_anomali.filter((x) => x !== g))}>✕</button>
              </span>
            ))}
            <input type="date" className={`${inputCls} w-44`} value={nuovoGiorno} data-testid="lungo-anomalo-input"
              onChange={(e) => setNuovoGiorno(e.target.value)} />
            <button type="button" data-testid="lungo-anomalo-aggiungi" disabled={!nuovoGiorno}
              onClick={() => { setL("giorni_anomali", [...new Set([...(cfg.lungo.giorni_anomali || []), nuovoGiorno])]); setNuovoGiorno(""); }}
              className={`${bottone} border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-40`}>
              Aggiungi giorno
            </button>
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-5" data-testid="proiezioni-corto">
        <div>
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-[#0E7490]">Canale corto</h3>
          <p className="text-sm text-slate-500 mt-1">
            Previsione anno = liquidato ÷ giorni trascorsi × giorni dell'anno. Quarter e mese: il giorno anomalo
            esce dalla base (−1 giorno) e viene risommato a consuntivo.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm" data-testid="formula-corto-anno">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Previsione chiusura anno · liquidato</div>
            <div className="font-mono text-slate-800">
              {eurShort(P.liquidato)} ÷ {C.giorni.giorni_lavorati_anno} {unita(C.metodo)} × {C.giorni.giorni_lavorativi_anno} {unita(C.metodo)}
              {" = "}<b>{eurShort(P.proiezione_anno)}</b> · budget {eurShort(P.budget_liquidato)} ({num(P.raggiungimento_perc, 2)}% raggiunto)
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm" data-testid="formula-corto-mese">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Previsione chiusura {MESI[P.mese - 1]} · liquidato</div>
            <div className="font-mono text-slate-800">
              (liquidato mese{C.giorno_anomalo_attivo && C.anomalo_valore ? ` − ${eurShort(C.anomalo_valore)} giorno anomalo` : ""})
              {" ÷ "}{C.giorni.giorni_lavorati_mese} × {C.giorni.giorni_lavorativi_mese}
              {C.giorno_anomalo_attivo && C.anomalo_valore ? ` + ${eurShort(C.anomalo_valore)}` : ""}
              {" = "}<b>{eurShort(P.proiezione_mese_importo)}</b> · {num(P.proiezione_mese_pratiche)} pratiche
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
          <Scelta label="Metodo di proiezione" value={cfg.corto.metodo} onChange={(v) => setC("metodo", v)}
            opzioni={dati.metodi} testId="corto-metodo" />
          <div>
            <label className="text-xs text-slate-500 block mb-1">Giorno anomalo isolato</label>
            <input type="date" className={inputCls} value={cfg.corto.giorno_anomalo || ""} data-testid="corto-anomalo-data"
              onChange={(e) => setC("giorno_anomalo", e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 pt-6">
            <input type="checkbox" checked={!!cfg.corto.giorno_anomalo_attivo} data-testid="corto-anomalo-attivo"
              onChange={(e) => setC("giorno_anomalo_attivo", e.target.checked)} />
            Isola il giorno anomalo {C.anomalo_valore ? `(${eurShort(C.anomalo_valore)})` : ""}
          </label>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Perimetro del liquidato, box per box</div>
          <table className="text-sm" data-testid="corto-perimetro-box">
            <thead>
              <tr className="text-xs text-slate-500">
                <th className="text-left pr-6 py-1 font-medium">Box</th>
                <th className="px-4 py-1 font-medium">Operatrici del lungo</th>
                <th className="px-4 py-1 font-medium">Vivibanca</th>
              </tr>
            </thead>
            <tbody>
              {[["budget_perc", "% raggiungimento budget liquidato"], ["previsione_anno", "Previsione chiusura anno"],
                ["previsione_mese_pratiche", "Previsione mese · pratiche liquidate"],
                ["previsione_mese_importo", "Previsione mese · importo liquidato"]].map(([box, nome]) => {
                const per = { fuori_perimetro: !!cfg.corto.includi_fuori_perimetro, vivibanca: !!cfg.corto.includi_vivibanca,
                  ...((cfg.corto.perimetro_box || {})[box] || {}) };
                const set = (k, v) => setC("perimetro_box", { ...(cfg.corto.perimetro_box || {}), [box]: { ...per, [k]: v } });
                return (
                  <tr key={box} className="border-t border-slate-100">
                    <td className="pr-6 py-2 text-slate-700">{nome}</td>
                    <td className="px-4 py-2 text-center">
                      <input type="checkbox" checked={per.fuori_perimetro} data-testid={`perimetro-${box}-fuori`}
                        onChange={(e) => set("fuori_perimetro", e.target.checked)} />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <input type="checkbox" checked={per.vivibanca} data-testid={`perimetro-${box}-vb`}
                        onChange={(e) => set("vivibanca", e.target.checked)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex justify-end">
        <button onClick={salva} disabled={salvataggio} data-testid="proiezioni-salva"
          className={`${bottone} bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50`}>
          {salvataggio ? "Salvataggio…" : "Salva parametri"}
        </button>
      </div>
    </div>
  );
}
